import produce from "immer";
import { each } from "lodash";
import { AbstractDate, DateMath } from "@linkfluence/models/date";
import { Facet } from "@linkfluence/models/facet";
import { Metric } from "@linkfluence/models/metric";
import { Timezone } from "@linkfluence/models/timezone";
import {
  getIntervalFromDates,
  getLastYearPeriod,
  getPreviousPeriod,
  setNewTimezone,
} from "@tootools/date";
import { Clause } from "../clause";
import {
  getDateFromClause,
  getWorkspaceIdCtxFromClause,
} from "../clause/getter";
import {
  refineClauseWithDate,
  refineClauseWithInnerClause,
} from "../clause/refiner";
import { Dataviz } from "../dataviz";
import { FullFacet } from "../facets";
import { getComparaisonPeriodFromFragments } from "../fragment";
import {
  AIFragment,
  PivotFields,
  PivotTerms,
  SearchFragment,
  Sort,
  SortInfluencer,
  getTermFromAggs,
} from "../search";
import { getFragmentWithContext } from "./fragment";
import {
  isCardWithRequest,
  isDateMetricCard,
  isDateMetricsCard,
  isDateTermCard,
  isEmbeddedCard,
  isPinnedAuthorCard,
  isPinnedPostCard,
  isTextCard,
} from "./is";
import {
  AICard,
  Card,
  DateMetricCard,
  DemographicCard,
  EmbeddedCard,
  PinnedAuthorCard,
  PivotCard,
  PostsCard,
  TextCard,
  UsersCard,
} from "./model";
import { ComparisonPeriod } from "./period";
import { CardTerm, getTermFromCard } from "./term";
import { cardAdapter, cardFacade } from "./types";
import { TimeUnit } from "./types/time-unit";
import { PinnedPostCard, TermCard } from ".";

// TITLE
export function editTitleOnCard<T extends Card>(card: T, title: string) {
  return produce(card, (draft) => {
    draft.title = title;
  });
}

const isWithoutPeriodCard = (card: Card) =>
  isEmbeddedCard(card) ||
  isTextCard(card) ||
  isPinnedPostCard(card) ||
  isPinnedAuthorCard(card);

// PERIOD
export function setCardPeriod<T extends Card>(
  card: T,
  period: { from: AbstractDate; to?: AbstractDate }
) {
  const interval = getIntervalFromDates(period.from, period.to || "now", "UTC");
  return produce(card, (draft) => {
    if (isWithoutPeriodCard(draft)) {
      return null;
    }
    if (isPinnedAuthorCard(draft)) return null;
    if (
      isDateMetricCard(draft) ||
      isDateMetricsCard(draft) ||
      isDateTermCard(draft)
    ) {
      draft.fragments[0].request.aggs.date.interval = interval;
    }
    if (draft.fragments[1]) {
      if (
        isDateMetricCard(draft) ||
        isDateMetricsCard(draft) ||
        isDateTermCard(draft)
      ) {
        draft.fragments[1].request.aggs.date.interval = interval;
      }
      const comparisonPeriod = getComparaisonPeriodFromFragments(
        draft.fragments[0],
        draft.fragments[1]
      );
      draft.fragments[0].request.query = refineClauseWithDate(
        draft.fragments[0].request.query,
        {
          from: period.from,
          to: period.to || "now",
        }
      );
      if (comparisonPeriod === "previous_period") {
        const previousPeriod = getPreviousPeriod({
          from: period.from,
          to: period.to || "now",
        });
        draft.fragments[1].request.query = refineClauseWithDate(
          draft.fragments[1].request.query,
          {
            from: previousPeriod.from || DateMath.Last_30_Days,
            to: previousPeriod.to || "now",
          }
        );
      } else if (comparisonPeriod === "last_year") {
        const lastYearPeriod = getLastYearPeriod({
          from: period.from,
          to: period.to || "now",
        });
        draft.fragments[1].request.query = refineClauseWithDate(
          draft.fragments[1].request.query,
          {
            from: lastYearPeriod.from || DateMath.Last_30_Days,
            to: lastYearPeriod.to || "now",
          }
        );
      }
    } else {
      draft.fragments[0].request.query = refineClauseWithDate(
        draft.fragments[0].request.query,
        {
          from: period.from,
          to: period.to || "now",
        }
      );
    }
  });
}

export function setCardTimezone<T extends Card>(
  card: T,
  previousTimezone: Timezone,
  timezone: Timezone
) {
  return produce(card, (draft) => {
    if (draft && isCardWithRequest(draft)) {
      each(draft.fragments, (fragment: AIFragment | SearchFragment) => {
        const { from, to } = getDateFromClause(fragment.request.query);
        fragment.request.timezone = timezone;
        fragment.request.query = refineClauseWithDate(fragment.request.query, {
          from: setNewTimezone(from, previousTimezone, timezone),
          to: setNewTimezone(to, previousTimezone, timezone),
        });
      });
    }
  });
}

function getClauseWithDateAndWorkspaceId(
  existingClause: Clause,
  nextClause: Clause,
  workspaceId: number
) {
  const clauseWithDate = refineClauseWithDate(
    nextClause,
    getDateFromClause(existingClause)
  );
  if (workspaceId) {
    return refineClauseWithInnerClause(clauseWithDate, {
      dashboard: workspaceId,
    });
  }
  return clauseWithDate;
}

// CLAUSE (@TODO add tests)
export function setCardClause<T extends Card>(
  card: T,
  clause: Clause,
  projectFocuses: number[]
) {
  if (!isCardWithRequest(card)) {
    return card;
  }
  return produce(card, (draft) => {
    const workspaceId = getWorkspaceIdCtxFromClause(
      draft.fragments[0].request.query
    );
    draft.fragments[0].request.query = getClauseWithDateAndWorkspaceId(
      draft.fragments[0].request.query,
      clause,
      workspaceId
    );
    //fix: properly set aggs when updating clause
    draft.fragments[0] = getFragmentWithContext(
      draft.fragments[0],
      workspaceId,
      projectFocuses
    );
    if (draft.fragments[1]) {
      draft.fragments[1].request.query = getClauseWithDateAndWorkspaceId(
        draft.fragments[1].request.query,
        clause,
        workspaceId
      );
      //@ts-ignore
      draft.fragments[1] = getFragmentWithContext(
        draft.fragments[1],
        workspaceId,
        projectFocuses
      );
    }
  });
}

export function editDatavizOptionsOnCard<T extends Card>(
  card: T,
  dataviz: T["dataviz"]
) {
  return produce(card, (draft) => {
    draft.dataviz = dataviz;
  });
}

export function editFieldOnPivotCard(
  card: PivotCard,
  term: PivotTerms,
  nextTerm: Facet
) {
  return produce(card, (draft) => {
    const pivot = getTermFromCard(draft);
    const against = getTermFromAggs(
      draft.fragments[0].request.aggs[pivot].aggs
    );
    if (term === PivotFields.Pivot) {
      draft.fragments[0].request.aggs[nextTerm] =
        draft.fragments[0].request.aggs[pivot];
      delete draft.fragments[0].request.aggs[pivot];
    } else if (term === PivotFields.Against) {
      draft.fragments[0].request.aggs[pivot].aggs[nextTerm] =
        draft.fragments[0].request.aggs[pivot].aggs[against];
      delete draft.fragments[0].request.aggs[pivot].aggs[against];
    }
  });
}

export function setCardPivotTerm(
  workspaceId: number,
  projectFocuses: number[],
  card: Card,
  nextTerm: CardTerm,
  facets: Record<string, FullFacet>
) {
  return cardFacade.setCardPivotTerm(
    workspaceId,
    projectFocuses,
    card,
    nextTerm,
    facets
  );
}

export function setCardDataviz(
  workspaceId: number,
  projectFocuses: number[],
  card: Card,
  dataviz: Dataviz["name"]
) {
  return cardFacade.setCardDataviz(workspaceId, projectFocuses, card, dataviz);
}

export function setCardTerm(
  workspaceId: number,
  projectFocuses: number[],
  card: Card,
  nextTerm: CardTerm,
  facets: Record<string, FullFacet>
) {
  return cardFacade.setCardTerm(
    workspaceId,
    projectFocuses,
    card,
    nextTerm,
    facets
  );
}

export function setCardTimeUnit<C extends Card>(
  card: C,
  timeUnit: TimeUnit
): C {
  return cardFacade.setCardTimeUnit(card, timeUnit) as C;
}

export function setCardPrimaryMetric<C extends Card>(
  workspaceId: number,
  projectFocuses: number[],
  card: C,
  metric: Metric
): C {
  return cardFacade.setCardPrimaryMetric(
    workspaceId,
    projectFocuses,
    card,
    metric
  ) as C;
}

export function setCardSecondaryMetric<C extends Card>(
  card: C,
  metric: Metric
): C {
  return cardFacade.setCardSecondaryMetric(card, metric) as C;
}

export function setCardTertiaryMetric<C extends Card>(
  card: C,
  metric: Metric
): C {
  return cardFacade.setCardTertiaryMetric(card, metric) as C;
}

export function setCardComparaisonPeriod<C extends Card>(
  workspaceId: number,
  projectFocuses: number[],
  card: C,
  comparisonPeriod: ComparisonPeriod
): C {
  return cardFacade.setCardComparaisonPeriod(
    workspaceId,
    projectFocuses,
    card,
    comparisonPeriod
  ) as C;
}

// Adapter

export function switchToDemographicCard(
  workspaceId: number,
  projectFocuses: number[],
  card: Card,
  options?: {
    metric?: Metric;
    dataviz?: DemographicCard["dataviz"];
    comparisonPeriod?: ComparisonPeriod;
  }
) {
  return cardAdapter.adaptToDemographicCard(
    workspaceId,
    projectFocuses,
    card,
    options
  );
}

export function switchToDateMetricCard(
  workspaceId: number,
  projectFocuses: number[],
  card: Card,
  options?: {
    metric?: Metric;
    dataviz?: DateMetricCard["dataviz"];
    comparisonPeriod?: ComparisonPeriod;
  }
) {
  return cardAdapter.adaptToDateMetricCard(
    workspaceId,
    projectFocuses,
    card,
    options
  );
}

export function switchToTermCard(
  workspaceId: number,
  projectFocuses: number[],
  card: Card,
  options?: {
    metric?: Metric;
    dataviz?: TermCard["dataviz"];
    comparisonPeriod?: ComparisonPeriod;
  }
) {
  return cardAdapter.adaptToTermCard(
    workspaceId,
    projectFocuses,
    card,
    options
  );
}

export function editSortOnPostCard(card: PostsCard, sort: Sort["on"]) {
  return produce(card, (draft) => {
    draft.fragments[0].request.pagination.sort.on = sort;
  });
}

export function editSortOnUserCard(card: UsersCard, sort: SortInfluencer) {
  return produce(card, (draft) => {
    draft.fragments[0].request.aggs.users.sort.on = sort;
  });
}

// EMBEDDED
export function editURLOnCard<T extends EmbeddedCard>(card: T, url: string) {
  return produce(card, (draft) => {
    draft.dataviz.url = url;
  });
}

export function editDisplayLinkOnCard<T extends EmbeddedCard>(
  card: T,
  displayLink: boolean
) {
  return produce(card, (draft) => {
    draft.dataviz.displayLink = displayLink;
  });
}

export function editImageSizeOnCard<T extends EmbeddedCard>(
  card: T,
  size: T["dataviz"]["size"]
) {
  return produce(card, (draft) => {
    draft.dataviz.size = size;
  });
}

// TEXT
export function editTextOnCard<T extends TextCard>(card: T, content: string) {
  return produce(card, (draft) => {
    draft.dataviz.content = content;
  });
}

// PINNED POST
export function editPostIdOnCard<T extends PinnedPostCard>(
  workspaceId: number,
  projectFocuses: number[],
  card: T,
  postId: string
) {
  return cardFacade.setCardPostId(workspaceId, projectFocuses, card, postId);
}

// PINNED AUTHOR
export function editAuthorIdOnCard<T extends PinnedAuthorCard>(
  card: T,
  authorId: string
) {
  return produce(card, (draft) => {
    draft.dataviz.authorId = authorId;
    draft.fragments[0].authorId = authorId;
  });
}

// AI
export function editAITypeOnCard(
  card: AICard,
  analysisType: AIFragment["analysis"]["type"],
  title?: string
) {
  return produce(card, (draft) => {
    draft.fragments[0].analysis = {
      includeNegativeAnalysis: false,
      type: analysisType,
      useInsightPageAIContext: true,
    };

    if (title) {
      draft.title = title;
    }
  });
}
export function editAINegativeAnalysisOnCard(
  card: AICard,
  aiNegativeAnalysis: AIFragment["analysis"]["includeNegativeAnalysis"]
) {
  return produce(card, (draft) => {
    draft.fragments[0].analysis = {
      ...draft.fragments[0].analysis,
      includeNegativeAnalysis: aiNegativeAnalysis,
    };
  });
}
